import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Button, Divider } from '@mui/material';
import { Helmet } from 'react-helmet-async';

import LoadingSpinner from 'components/LoadingSpinner';
import ErrorPanel from 'components/ErrorPanel';
import { ROUTES } from 'enum';
import { clearError as clearLogoutError } from 'store/user/actions';
import { errorSelector as logoutErrorSelector } from 'store/user/selectors';
import { createErrorMessageSelector, createLoadingSelector, needsLoginMessageSelector } from 'store/utils/selectors';
import { LOGIN_EMAIL, LOGIN_PASSWORD } from 'assets/Icons';
import usePrevious from 'hooks/usePrevious';
import { login, removeAuthError } from './scenario-actions';

import './login-form.scss';

const loadingSelector = createLoadingSelector(['LOGIN']);
const errorSelector = createErrorMessageSelector(['LOGIN']);

function LoginForm() {
  const history = useHistory();
  const location = useLocation();

  const dispatch = useDispatch();
  const isLoading = useSelector(loadingSelector);
  const isLoadingUser = useSelector((state) => state.auth.loadingUser);
  const error = useSelector(errorSelector);
  const logoutError = useSelector(logoutErrorSelector);
  const needsLoginMessage = useSelector(needsLoginMessageSelector);

  const prevLogoutError = usePrevious(logoutError);
  const intervalRef = useRef(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [authError, setAuthError] = useState('Please log in!');
  const [from, setFrom] = useState(() =>
    location.state && location.state.from ? location.state.from : { pathname: '/v1' },
  );

  const clearError = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    dispatch(clearLogoutError());
  }, [dispatch]);

  useEffect(() => {
    dispatch(removeAuthError());
    intervalRef.current = setInterval(() => {
      clearError();
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [clearError, dispatch]);

  useEffect(() => {
    if (logoutError) {
      setAuthError(logoutError);
      setFrom({ pathname: '/v1' });
    }
  }, [logoutError]);

  useEffect(() => {
    if (error === 'MFA required') {
      setMfaEnabled(true);
    }
  }, [error]);

  useEffect(() => {
    if (!logoutError && prevLogoutError !== logoutError && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [logoutError, prevLogoutError]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (mfaEnabled) {
      dispatch(login({ email, password, code }, from));
      return;
    }

    dispatch(login({ email, password }, from));
  };

  const removeErrorMessage = () => {
    if (logoutError) {
      dispatch(clearLogoutError());
      return;
    }

    setAuthError('');
  };

  return (
    <>
      <Helmet>
        <title>Log in on Sectrify</title>
      </Helmet>
      <div className="login-form-page--content">
        <div className="login-form">
          <h2 className="title">{mfaEnabled ? 'Two-Factor Authentication (2FA)' : 'Log in'}</h2>
          {error && error !== 'MFA required' && <div className="alert alert-danger">{error}</div>}
          {logoutError && <div className="alert alert-warning">{logoutError}</div>}
          {mfaEnabled ? (
            <form onSubmit={handleSubmit} autoComplete="off">
              <p className="mfa-label">Enter the six-digit code you see in your Two-Factor Authentication app.</p>
              <div className="input-container">
                <input
                  name="code"
                  placeholder="MFA Code"
                  required
                  autoComplete="off"
                  onChange={(event) => setCode(event.target.value)}
                  onFocus={removeErrorMessage}
                />
              </div>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isLoading || isLoadingUser}
                sx={{ fontSize: 18, p: 2 }}
              >
                Continue
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} autoComplete="off">
              {needsLoginMessage && (
                <ErrorPanel message={authError} buttonClickHandler={removeErrorMessage} dataTestId="login-error" />
              )}
              <div className="input-container">
                <div className="icon-container">
                  <img src={LOGIN_EMAIL} className="input-icon" alt="input-icon" />
                </div>
                <input
                  name="email"
                  placeholder="Email"
                  required
                  autoComplete="off"
                  onChange={(event) => setEmail(event.target.value)}
                  onFocus={removeErrorMessage}
                />
              </div>
              <div className="input-container">
                <div className="icon-container">
                  <img src={LOGIN_PASSWORD} className="input-icon" alt="input-icon" />
                </div>
                <input
                  name="password"
                  placeholder="Password"
                  type="password"
                  required
                  onChange={(event) => setPassword(event.target.value)}
                  onFocus={removeErrorMessage}
                />
              </div>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isLoading || isLoadingUser}
                sx={{ fontSize: 18, p: 2 }}
                data-testid="btn-login"
              >
                Log in
              </Button>
              <div className="login-links">
                <Link to={ROUTES.SIGN_UP_EMAIL}>
                  <span className="sign sign-link">Register here</span>
                </Link>
                <Link to={ROUTES.PWD_RESET_EMAIL}>
                  <span className="sign sign-link">Reset password</span>
                </Link>
              </div>
              <Divider sx={{ my: 2 }} />
              <Button
                fullWidth
                variant="contained"
                onClick={() => history.push(ROUTES.LOGIN_METHODS)}
                sx={{ fontSize: 18, mt: 2, p: 2 }}
                data-testid="btn-sso"
              >
                Authenticate another way
              </Button>
            </form>
          )}
        </div>
      </div>
      {isLoadingUser && <LoadingSpinner />}
    </>
  );
}

export default LoginForm;
